import React, { useState, useEffect } from "react";
import Select from "react-select";
import { getUsersSummary } from "../../../../../api/api";
import { selectStyles } from "../../../../../utils/selectStyles";
import "./ApprovalModal.css";

const getUserFullName = (user) =>
  [user?.surname, user?.name].filter(Boolean).join(" ").trim();

const getPositionTitleFromUser = (user) => {
  const parts = [];
  if (user?.access_levels?.length) {
    user.access_levels.forEach((l) => {
      const name = typeof l === "string" ? l : l?.name;
      if (name) parts.push(name);
    });
  }
  if (user?.roles?.length) {
    user.roles.forEach((r) => {
      const name = typeof r === "string" ? r : r?.name;
      if (name) parts.push(name);
    });
  }
  return [...new Set(parts)].filter(Boolean).join(" / ") || "";
};

const ApprovalModal = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  title = "Согласование сметы",
  warning =
    "Подтвердите отправку сметы на согласование. После этого ее нельзя будет редактировать.",
}) => {
  const [approvers, setApprovers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!open) return;
    setApprovers([]);
    setSelectedUserToAdd(null);
    setComment("");
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setUsersLoading(true);
      try {
        const res = await getUsersSummary({ isActive: true, pageSize: 1000 });
        setUsersList(res?.users || []);
      } catch (e) {
        console.error("Ошибка загрузки списка пользователей:", e);
        setUsersList([]);
      } finally {
        setUsersLoading(false);
      }
    };
    load();
  }, [open]);

  const addApproverFromUser = (user) => {
    if (!user || approvers.some((a) => Number(a.id) === Number(user.id)))
      return;
    const name = getUserFullName(user) || "Без имени";
    const positionTitle = getPositionTitleFromUser(user);
    setApprovers((prev) => [
      ...prev,
      { id: user.id, title: positionTitle || "—", name },
    ]);
    setSelectedUserToAdd(null);
  };

  const handleRemoveApprover = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setApprovers((prev) =>
      prev.filter((a) => Number(a.id) !== Number(id)),
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const description = comment.trim();
    if (!description) {
      alert("Укажите описание для согласования");
      return;
    }
    onSubmit && onSubmit(approvers.map((a) => a.id), description);
  };

  if (!open) return null;

  return (
    <>
      <div className="approval-modal-backdrop" onClick={onClose} />
      <div className="approval-modal">
        <div className="approval-modal-title">{title}</div>
        <div className="approval-modal-warning">
          <span className="approval-modal-warning-icon">⚠️</span>
          <span>{warning}</span>
        </div>
        <div className="approval-modal-label">
          Выберите кому отправить на согласование:
        </div>
        <form onSubmit={handleSubmit}>
          <div className="approval-modal-list">
            {approvers.map((a) => (
              <div key={a.id} className="approval-modal-list-row">
                <div className="approval-modal-approver-info">
                  <span className="approval-checkbox-title">{a.title}</span>
                  <span className="approval-checkbox-name">{a.name}</span>
                </div>
                <button
                  type="button"
                  className="approval-modal-remove-btn"
                  onClick={(e) => handleRemoveApprover(e, a.id)}
                  disabled={isSubmitting}
                  title="Удалить из списка"
                  aria-label="Удалить из списка"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="approval-modal-add-user">
            <div className="approval-modal-add-user-label">
              Добавить согласующего из списка пользователей:
            </div>
            <Select
              isClearable
              placeholder={
                usersLoading ? "Загрузка..." : "Выберите пользователя"
              }
              isDisabled={usersLoading || isSubmitting}
              options={usersList
                .filter(
                  (u) =>
                    !approvers.some(
                      (a) => Number(a.id) === Number(u.id),
                    ),
                )
                .map((u) => ({
                  value: u.id,
                  label: getUserFullName(u) || "Без имени",
                  userData: u,
                }))}
              value={selectedUserToAdd}
              onChange={(opt) => {
                setSelectedUserToAdd(opt);
                if (opt?.userData) addApproverFromUser(opt.userData);
              }}
              styles={selectStyles}
              className="approval-modal-user-select"
            />
          </div>
          <label className="approval-modal-comment-label" htmlFor="estimate-approval-comment">
            Описание
          </label>
          <textarea
            id="estimate-approval-comment"
            className="approval-modal-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Кратко опишите, что нужно согласовать"
            rows={3}
            disabled={isSubmitting}
            required
          />
          <div className="approval-modal-actions">
            <button
              type="button"
              className="approval-btn outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="approval-btn green"
              disabled={
                isSubmitting ||
                approvers.length === 0 ||
                !comment.trim()
              }
            >
              {isSubmitting ? "Отправка..." : "Отправить"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ApprovalModal;
